import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { StarterPack } from '@b4b/shared';
import { useFeeds, useSubscribe, useSubscribePack } from '../api/queries.js';
import { packSubscription, usePackGroups } from '../lib/packs.js';
import { domainOf } from '../lib/url.js';
import { describeDiscoverError } from '../lib/discoverError.js';

/** Curated packs from starter-packs.json on the Sources page: expand a pack, add feeds one by one
 * or the whole pack; feeds already subscribed show as such. Packs in the UI language come first. */
export default function RecommendedPacks() {
  const { t } = useTranslation();
  const { lang, groups } = usePackGroups();
  const feeds = useFeeds();
  const subscribe = useSubscribe();
  const subscribePack = useSubscribePack();
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);
  const [pendingPackId, setPendingPackId] = useState<string | null>(null);
  // last failure, shown under the pack it belongs to
  const [error, setError] = useState<{ packId: string; message: string } | null>(null);

  const subscribedUrls = useMemo(() => (feeds.data ?? []).map((f) => f.url), [feeds.data]);
  if (groups.length === 0) return null;

  const packName = (pack: StarterPack) => pack.name[lang] ?? pack.name.cs ?? pack.id;

  const addOne = (pack: StarterPack, feed: { url: string; title: string }) => {
    setPendingUrl(feed.url);
    setError(null);
    subscribe.mutate(
      { feedUrl: feed.url, category: packName(pack), title: feed.title },
      {
        onError: (err) => setError({ packId: pack.id, message: describeDiscoverError(err, t) }),
        onSettled: () => setPendingUrl(null),
      },
    );
  };
  const addAll = (pack: StarterPack) => {
    setPendingPackId(pack.id);
    setError(null);
    subscribePack.mutate(pack.id, {
      onError: (err) => setError({ packId: pack.id, message: describeDiscoverError(err, t) }),
      onSettled: () => setPendingPackId(null),
    });
  };

  return (
    <section data-testid="recommended-packs" className="space-y-4">
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">{t('sources.recommended')}</h2>
        <p className="mt-1 text-xs text-neutral-500">{t('sources.recommendedHint')}</p>
      </div>
      {groups.map((group) => (
        <div key={group.key} className="space-y-2">
          <h3 className="text-xs font-medium uppercase tracking-wider text-neutral-400">{t(`feed.empty.${group.key}`)}</h3>
          {group.packs.map((pack) => {
            const status = packSubscription(pack, subscribedUrls);
            const packPending = pendingPackId === pack.id;
            return (
              <details
                key={pack.id}
                data-pack-id={pack.id}
                // present (empty) when every feed is followed, absent otherwise — the e2e asserts on it
                data-complete={status.complete ? '' : undefined}
                className="group rounded-xl border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-900"
              >
                <summary className="flex min-h-[52px] cursor-pointer list-none items-center gap-3 px-3 py-2 [&::-webkit-details-marker]:hidden">
                  <span aria-hidden className="text-lg">
                    {pack.emoji}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{packName(pack)}</span>
                    <span className="block text-xs text-neutral-500" data-pack-status>
                      {status.complete
                        ? t('sources.allSubscribed')
                        : t('sources.subscribedCount', { count: status.subscribed, total: status.total })}
                    </span>
                  </span>
                  <span aria-hidden className="text-neutral-400 transition-transform group-open:rotate-180">
                    ⌄
                  </span>
                </summary>
                <div className="border-t border-neutral-200 dark:border-neutral-800">
                  {!status.complete && (
                    <div className="flex items-center justify-end px-3 py-2">
                      <button
                        type="button"
                        onClick={() => addAll(pack)}
                        disabled={packPending}
                        data-add-all
                        className="min-h-[44px] rounded-lg bg-accent px-4 text-sm font-medium text-white hover:bg-accent-strong disabled:opacity-50"
                      >
                        {t('sources.addAll')}
                      </button>
                    </div>
                  )}
                  {error?.packId === pack.id && (
                    <p role="alert" className="px-3 pb-2 text-sm text-red-600 dark:text-red-400">
                      {error.message}
                    </p>
                  )}
                  <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
                    {pack.feeds.map((feed) => {
                      const subscribed = status.isSubscribed(feed.url);
                      return (
                        <li key={feed.url} className="flex items-center gap-3 px-3 py-1.5" data-feed-title={feed.title}>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium">{feed.title}</p>
                            <p className="truncate text-xs text-neutral-500">{domainOf(feed.url)}</p>
                          </div>
                          {subscribed ? (
                            <span className="text-xs font-medium text-accent" data-subscribed>
                              ✓ {t('sources.subscribed')}
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => addOne(pack, feed)}
                              disabled={pendingUrl === feed.url || packPending}
                              aria-label={t('sources.subscribeOne', { title: feed.title })}
                              className="min-h-[44px] min-w-[44px] rounded-lg border border-neutral-200 text-lg leading-none text-accent hover:bg-accent/10 disabled:opacity-50 dark:border-neutral-700"
                            >
                              +
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </details>
            );
          })}
        </div>
      ))}
    </section>
  );
}
